import {Button, Form, Modal} from "react-bootstrap";
import React, {useMemo} from "react";
import {Field, Formik} from "formik";
import {download, FormikControl, FormRowGroup, Loading, Oracle} from "../Components";
import API, {DataAPI} from "../../services/API";
import {useHandleClose} from "../../commons/ModalHelpers";
import Reports from "../Reports";
import {useLocation} from "react-router-dom";
import {Validation} from "../../commons/Utils";
import {hasSelectableValue, toPrinterOption} from "../../commons/DistributionUtils";

const FormikOracleField = ({name, onSearch, onValueChange, ...props}) => (
    <Field name={name}>
        {({ field, form, meta }) => {
            const value = field.value || null;
            const optionId = value && typeof value === 'object' ? value.id : value;
            const optionLabel = value && typeof value === 'object' ? value.label : '';
            const handleChange = (selected) => {
                const nextValue = selected || null;
                form.setFieldValue(field.name, nextValue);
                form.setFieldTouched(field.name, true, false);
                if (typeof onValueChange === 'function') {
                    onValueChange(nextValue, form);
                }
            }

            return <>
                <Oracle
                    key={`${name}-${optionId || 'empty'}-${optionLabel || ''}`}
                    {...field}
                    {...props}
                    value={value}
                    onSearch={onSearch}
                    onChange={handleChange}
                />
                {(meta.touched && meta.error) ?
                    <div className="feedback-invalid">{meta.error}</div>
                    : null}
            </>
        }}
    </Field>
)

const GenerateReport = () => {

    let location = useLocation();
    const handleClose = useHandleClose(Reports.url);
    const state = location && location.state ? location.state : {};
    const report = state.report || null;
    const r = report;
    const reportParams = r ? r.params : [];

    const getLocations = (filter) => {
        return API.getLocations(filter).then(res=>res.data);
    }

    const getPrinters = (filter) => {
        return API.getPrintersByQuery(filter).then(res => (res.data || []).map(toPrinterOption));
    }

    const locationParam = reportParams.find(param => param.type === 'location') || null;
    const printerParam = reportParams.find(param => param.type === 'printer') || null;
    const mainParams = reportParams.filter(param => param.type !== 'location' && param.type !== 'printer');

    const initial = useMemo(() => {
        let initial = {};
        for (let param of reportParams) {
            switch (param.type) {
                case 'date': {
                    initial[param.id] = new Date();
                    break;
                }
                case 'location':
                case 'printer': {
                    initial[param.id] = null;
                    break;
                }
                default:
                    initial[param.id] = '';
            }
        }
        return initial;
    }, [reportParams]);

    const renderMainParam = (param, idx) => {
        switch (param.type) {
            case 'date': {
                return <FormRowGroup key={idx} controlId={param.name} label={param.name}>
                    <FormikControl name={param.id} type="date"/>
                </FormRowGroup>
            }
            default:
                return <p key={idx}>Nieobsługiwany typ parametru: {param.type}</p>
        }
    }

    const convert = (value) => {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value.getTime();
        }
        if (value && typeof value === 'object' && value.id) {
            return value.id;
        }
        if (value === '') {
            return null;
        }
        return value;
    }

    const submit = async (values, {setSubmitting}) => {
        let params = Object.keys(values).map(v=>{
            return {
                id: v,
                value: convert(values[v])
            }
        })
        try {
            setSubmitting(true);
            let response = await DataAPI.getReport({
                reportId: r.id,
                params
            });
            download(response.name, response.data);
            handleClose();
        } finally {
            setSubmitting(false);
        }
    }

    const isValidDateValue = (value) => value instanceof Date && !Number.isNaN(value.getTime());

    const validate = (values) => {
        let err = {};
        for (let param of reportParams) {
            if (param.type === 'date') {
                Validation.add(err, param.id, isValidDateValue(values[param.id]) ? null : 'Niepoprawna data');
            }
        }

        if (locationParam && printerParam && hasSelectableValue(values[locationParam.id]) && hasSelectableValue(values[printerParam.id])) {
            Validation.add(err, locationParam.id, 'Wybierz lokację albo urządzenie');
            Validation.add(err, printerParam.id, 'Wybierz lokację albo urządzenie');
        }

        const from = values.fromDate;
        const to = values.toDate;
        if (isValidDateValue(from) && isValidDateValue(to) && from.getTime() > to.getTime()) {
            Validation.add(err, 'toDate', 'Data końcowa nie może być wcześniejsza niż początkowa');
        }
        return err;
    }

    if (!report) {
        return <Modal size="md" show={true} onHide={handleClose} animation={false}>
            <Modal.Header closeButton>
                <Modal.Title>Nie udało się otworzyć formularza raportu</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Brak definicji raportu. Zamknij okno i spróbuj ponownie z listy raportów.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleClose}>Wróć do raportów</Button>
            </Modal.Footer>
        </Modal>
    }

    return <Modal size="md" show={true} onHide={handleClose} animation={false}>
        <Formik initialValues={initial} onSubmit={submit} validate={validate} enableReinitialize={true}>
            {({ handleSubmit, isSubmitting, values }) => {
                const locationSelected = locationParam ? hasSelectableValue(values[locationParam.id]) : false;
                const printerSelected = printerParam ? hasSelectableValue(values[printerParam.id]) : false;

                return (
                <Form noValidate onSubmit={handleSubmit} className="form form-add-printer">
                    <Modal.Header closeButton>
                        <Modal.Title>{report.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {mainParams.map(renderMainParam)}
                        {(locationParam || printerParam) ? <>
                            {mainParams.length > 0 ? <hr/> : null}
                            <div className="mb-3">
                                <h5 className="mb-1">Filtrowanie</h5>
                                <p className="text-muted small mb-3">Wybierz lokację albo konkretne urządzenie.</p>
                                {locationParam ?
                                    <FormRowGroup controlId={locationParam.name} label={locationParam.name}>
                                        <FormikOracleField
                                            id="reportLocationId"
                                            name={locationParam.id}
                                            onSearch={getLocations}
                                            disabled={printerSelected}
                                            onValueChange={(selected, form) => {
                                                if (selected && printerParam) {
                                                    form.setFieldValue(printerParam.id, null);
                                                }
                                            }}
                                        />
                                    </FormRowGroup>
                                    : null}
                                {printerParam ?
                                    <FormRowGroup controlId={printerParam.name} label={printerParam.name}>
                                        <FormikOracleField
                                            id="reportPrinterId"
                                            name={printerParam.id}
                                            onSearch={getPrinters}
                                            disabled={locationSelected}
                                            placeholder="Wyszukaj urządzenie"
                                            promptText="Wpisz min. 2 znaki, aby wyszukać urządzenie"
                                            emptyLabel="Nie znaleziono urządzeń"
                                            onValueChange={(selected, form) => {
                                                if (selected && locationParam) {
                                                    form.setFieldValue(locationParam.id, null);
                                                }
                                            }}
                                        />
                                    </FormRowGroup>
                                    : null}
                            </div>
                        </> : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleClose}>Anuluj</Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? <Loading msg="Trwa generowanie"/> : <span>Generuj</span>}</Button>
                    </Modal.Footer>
                </Form>
            )}}
        </Formik>
    </Modal>
}

GenerateReport.url = '/reports/generate';
export default GenerateReport;
